import { css, StyleSheet } from 'aphrodite';
import moment from 'moment';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormFieldType } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Button } from '../../../web/components-v2/Button';
import { AnimatedLionGraphic } from '../../components/svgs/AnimatedLionGraphic';
import { HexGraphic } from '../../components/svgs/HexGraphic';
import { FormFieldResolvers } from '../../viewModels/FormFieldResolvers';
import { DealField } from './DealField';
import type { CreateDealResult, DealFieldDef } from './types';
import { useCreateDeal } from './useCreateDeal';
import { useDealForm } from './useDealForm';
import { useSaveDealNote } from './useSaveDealNote';
import { useUpdateDeal } from './useUpdateDeal';

/** Matches a bare GUID — used to tell an existing note id from new HTML content. */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Props for the `DealForm` orchestrator.
 *
 * `initialValues` lets the wrap-up flow seed obvious fields (e.g. a
 * description copied from the wrap-up note); `customProperties` is a
 * pass-through for context the legacy `CustomFormViewModel.save` sends
 * along (decisionMaker, etc.). `onCreated` fires with the
 * `CreateDealResult` once the submit resolves successfully — the same
 * callback covers both Create and Update paths so the wrap-up flow can
 * react identically to either.
 *
 * When `dealId` is provided the form switches to Update mode: schema is
 * fetched from `UpdateDeal/{dealId}` (with current values pre-populated),
 * the header reads "Update Deal", and submit PUTs to the same URL. Without
 * `dealId` the form behaves as before — Create Deal via POST.
 */
export type DealFormProps = {
	companyId: string;
	/**
	 * When set, the form runs in Update mode against the existing deal —
	 * mirrors the legacy `lead.loadDealForm(dealId)` path used for the
	 * `NewDemo` / `AppointmentRescheduled` outcomes.
	 */
	dealId?: string;
	/**
	 * The lead's IANA timezone (`company.timeZone`). Passed through to the
	 * MeetingScheduler field so demo booking happens in the lead's timezone,
	 * not the rep's (Real-Magic/Levitate#19024).
	 */
	leadTimeZone?: string;
	/**
	 * Optional pre-fill of values, e.g. from the wrap-up step. Keyed by field
	 * NAME (the wrap-up seeds `ExecutiveSummaryNote` / `OutcomeNotes`); the
	 * hydration effect translates these to id-keys.
	 */
	initialValues?: Record<string, unknown>;
	/** Optional pass-through to useCreateDeal / useUpdateDeal (e.g. decisionMaker). */
	customProperties?: Record<string, unknown>;
	/**
	 * Phone call this deal is being created from. Sent on submit so the backend
	 * links the deal to the call (legacy `customFormData.phoneCallId`). `'manual'`
	 * and falsy values are dropped before sending.
	 */
	phoneCallId?: string;
	/** Lead-served source passthrough (legacy `ISkipLeadRequest.leadServedSource`). */
	leadServedSource?: string;
	onCancel?: () => void;
	onCreated?: (result: CreateDealResult) => void;
};

/**
 * Orchestrates the Create-Deal form.
 *
 * Loads the form schema via `useDealForm`, tracks per-field values in
 * local state seeded from each field's server-supplied `initialValue`
 * (parent-supplied `initialValues` take precedence), validates required
 * fields on Create, and POSTs via `useCreateDeal`. Loading and error
 * states render inline.
 */
export const DealForm: FC<DealFormProps> = ({
	companyId,
	dealId,
	leadTimeZone,
	initialValues,
	customProperties,
	phoneCallId,
	leadServedSource,
	onCancel,
	onCreated,
}) => {
	const isUpdate = !!dealId;
	const { config, isLoading, error: loadError } = useDealForm(companyId, dealId);
	const createDeal = useCreateDeal();
	const updateDeal = useUpdateDeal();
	const saveDealNote = useSaveDealNote();
	// Pick whichever mutation is active for this mode so loading state and the
	// disabled gating below can read from a single object. The other mutation
	// stays idle and never fires.
	const activeMutation = isUpdate ? updateDeal : createDeal;
	// The Note field(s) persist via `saveDealNote.mutateAsync` *before* the
	// create/update deal mutation fires, so gating only on `activeMutation`
	// leaves the form interactive during that phase — a double-click or mid-save
	// field edit re-enters `handleCreate` and POSTs a duplicate note (then a
	// second deal). Gate every interactive control on both.
	const isBusy = activeMutation.isPending || saveDealNote.isPending;
	const userSession = useUserSession();

	// Form state is keyed by the always-present field `id` (NOT `field.name`,
	// which is optional on `IFormField` and can be blank/duplicate across fields
	// — colliding keys made filled required fields read back empty and falsely
	// trip the "… is required" gate). Mirrors the legacy `CustomFormViewModel.save`,
	// which keys `formData.values` by `field.id`. Parent `initialValues` arrive
	// keyed by NAME and are translated to id-keys in the hydration effect.
	const [values, setValues] = useState<Record<string, unknown>>({});
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Scheduled-meeting id captured when the rep books via the auto scheduler.
	// When set, submit sends `meetingId` and drops the demo-perform-date value
	// (legacy `form.ts:534-537`).
	const [scheduledMeetingId, setScheduledMeetingId] = useState<string | undefined>(undefined);

	// note field id → saved note id, so a re-submit PUT-updates the same note
	// instead of creating a duplicate (the editor content can change between
	// failed attempts). Mirrors how legacy reuses `f.note.id`.
	const savedNoteIdsRef = useRef<Record<string, string>>({});

	// Reset saved-note ids when the form scope changes to a different deal, so a
	// stale id from the previous deal isn't PUT-updated. Keyed on `dealId` (the
	// scope boundary) rather than `config.fields` so a benign config refetch for
	// the *same* deal doesn't wipe an id mid-submit.
	useEffect(() => {
		savedNoteIdsRef.current = {};
	}, [dealId]);

	// Mirrors legacy `meetingSchedulerState.schedulingType === Automatic`
	// behaviour: once the rep clicks "Find Demo Spot" and the booking URL
	// resolves, the demo-perform-date and demo-performer fields are hidden
	// because the server populates them when the rep returns. Stays `false`
	// for the manual flow.
	const [schedulerEngaged, setSchedulerEngaged] = useState(false);

	// Hydrate values once the config arrives. State is keyed by `field.id`;
	// parent-supplied `initialValues` are keyed by field NAME (the wrap-up seeds
	// `ExecutiveSummaryNote` / `OutcomeNotes`), so translate name→id here. Parent
	// values win over the field's own server-supplied initial value. We only fill
	// keys that aren't already set and only mutate when something changed, to
	// avoid a render loop.
	useEffect(() => {
		if (!config) return;
		setValues(prev => {
			const next = { ...prev };
			let changed = false;
			for (const f of config.fields) {
				if (next[f.id] !== undefined) continue;
				const seeded =
					f.name && initialValues && initialValues[f.name] !== undefined ? initialValues[f.name] : f.initialValue;
				if (seeded !== undefined) {
					next[f.id] = seeded;
					changed = true;
				}
			}
			return changed ? next : prev;
		});
	}, [config, initialValues]);

	const handleFieldChange = useCallback((fieldId: string, next: unknown) => {
		setValues(prev => ({ ...prev, [fieldId]: next }));
		// Clear the field-level error as soon as the user edits the field —
		// matches the legacy form behaviour and avoids stale "is required"
		// messages while typing.
		setErrors(prev => {
			if (!prev[fieldId]) return prev;
			const { [fieldId]: _removed, ...rest } = prev;
			return rest;
		});
	}, []);

	// Resolve the contact id off the form's `Contact`-typed field (the
	// legacy form names this "Associated Contact"). The MeetingScheduler
	// field needs this so it can POST a one-shot demo-link request keyed on
	// the contact. We pick the first Contact-typed field — there is at most
	// one in the Create Deal flow.
	const associatedContactId = useMemo<string | undefined>(() => {
		if (!config) return undefined;
		const contactField = config.fields.find(f => f.fieldType === FormFieldType.Contact);
		if (!contactField) return undefined;
		const v = values[contactField.id];
		return typeof v === 'string' && v ? v : undefined;
	}, [config, values]);

	// Names of the demo-perform-date / demo-performer fields, resolved via
	// the same coffee-key mappings the legacy form uses. We hide these fields
	// once the auto scheduler has been engaged, because the server populates
	// them when the rep returns from booking — matching legacy behaviour.
	const visibleFields = useMemo<DealFieldDef[]>(() => {
		if (!config) return [];
		if (!schedulerEngaged) return config.fields;
		const demoPerformDateName = FormFieldResolvers.getDemoPerformDateField(userSession);
		const demoPerformerName = FormFieldResolvers.getDemoPerformerField(userSession);
		return config.fields.filter(f => f.name !== demoPerformDateName && f.name !== demoPerformerName);
	}, [config, schedulerEngaged, userSession]);

	const handleSchedulerEngaged = useCallback((meetingId?: string) => {
		setSchedulerEngaged(true);
		setScheduledMeetingId(meetingId);
	}, []);

	const handleSchedulerDisengaged = useCallback(() => {
		setSchedulerEngaged(false);
		setScheduledMeetingId(undefined);
	}, []);

	const handleCreate = useCallback(async () => {
		if (!config) return;
		// Re-entrancy guard (belt-and-suspenders with the disabled buttons): a
		// second invocation while the note save or deal mutation is in flight would
		// duplicate the note/deal.
		if (saveDealNote.isPending || createDeal.isPending || updateDeal.isPending) return;

		// Demo-perform-date / -performer are hidden (and server-populated) once
		// the auto scheduler books a meeting — skip them in validation, matching
		// the `visibleFields` filter and legacy `meetingSchedulerState` behaviour.
		const demoPerformDateName = FormFieldResolvers.getDemoPerformDateField(userSession);
		const demoPerformerName = FormFieldResolvers.getDemoPerformerField(userSession);
		const isHiddenSchedulerField = (f: DealFieldDef) =>
			schedulerEngaged && !!f.name && (f.name === demoPerformDateName || f.name === demoPerformerName);

		// Validate required, visible fields. State is keyed by `field.id`.
		const missing: Record<string, string> = {};
		const orderedMissingIds: string[] = [];
		for (const f of config.fields) {
			if (f.isHidden || isHiddenSchedulerField(f)) continue;
			const required = f.isOptional !== true;
			if (!required) continue;
			const v = values[f.id];
			if (v === undefined || v === null || v === '') {
				missing[f.id] = `${f.label} is required`;
				orderedMissingIds.push(f.id);
			}
		}
		if (orderedMissingIds.length > 0) {
			setErrors(missing);
			// Match legacy `DealDrawerContent.onActionClick` behaviour: scroll
			// the first invalid field into view so the rep can correct it.
			// Pulled out of render so SSR/JSDOM `document` access is gated by
			// the actual user click; the anchor is the stable `deal-field-${id}`
			// set by `DealField`. JSDOM does not implement `scrollIntoView`,
			// hence the `typeof` guard on the function itself.
			const firstId = orderedMissingIds[0];
			if (typeof document !== 'undefined') {
				const el = document.getElementById(`deal-field-${firstId}`);
				if (el && typeof el.scrollIntoView === 'function') {
					el.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
			}
			return;
		}

		// Serialize values per field type, mirroring legacy
		// `CustomFormViewModel.save` (`form.ts:476-554`):
		//  - Note  → save the note entity, send its `id` (never the HTML).
		//  - Date  → `moment().format()` (local offset, not UTC `Z`).
		//  - MeetingScheduler → never a value; the booked meeting is sent as the
		//    top-level `meetingId`, and the demo-perform-date is dropped.
		//  - everything else (incl. User/Contact/Option ids) → sent as-is.
		const serialized: Record<string, unknown> = {};
		for (const f of config.fields) {
			if (f.fieldType === FormFieldType.MeetingScheduler) continue;
			if (scheduledMeetingId && !!f.name && f.name === demoPerformDateName) continue;

			const raw = values[f.id];
			if (raw === undefined || raw === null || raw === '') continue;

			switch (f.fieldType) {
				case FormFieldType.Note: {
					const html = typeof raw === 'string' ? raw : '';
					const existingId = savedNoteIdsRef.current[f.id];
					// A bare GUID with no local save is an already-saved note seeded by
					// the Update-deal response — reference it as-is (editing existing
					// note content in place is a deferred follow-up).
					if (!existingId && GUID_RE.test(html)) {
						serialized[f.id] = html;
						break;
					}
					// Persist the note (POST new, or PUT the note saved on a prior
					// submit attempt to avoid duplicates) and send its id.
					const saved = await saveDealNote.mutateAsync({ companyId, contentHtml: html, noteId: existingId });
					savedNoteIdsRef.current[f.id] = saved.id;
					serialized[f.id] = saved.id;
					break;
				}
				case FormFieldType.Date:
				case FormFieldType.DateTime: {
					// Parse with an explicit format allowlist in strict mode — bare
					// `moment(raw)` falls back to flaky native Date parsing for
					// non-ISO strings. Drop an unparseable value rather than forward
					// garbage to the server (which 400s cryptically).
					const m = moment(raw as moment.MomentInput, [moment.ISO_8601, 'YYYY-MM-DD'], true);
					if (m.isValid()) serialized[f.id] = m.format();
					break;
				}
				default:
					serialized[f.id] = raw;
					break;
			}
		}

		const payload = {
			values: serialized,
			...(scheduledMeetingId ? { meetingId: scheduledMeetingId } : {}),
			// Legacy drops a 'manual' phone-call id (`DealDrawerContent/index.tsx:86`).
			...(phoneCallId && phoneCallId !== 'manual' ? { phoneCallId } : {}),
			...(leadServedSource ? { leadServedSource } : {}),
			customProperties,
		};

		// Fork on mode: Update PUTs to UpdateDeal/{dealId}; Create POSTs to
		// CreateDeal. Both resolve to the same `CreateDealResult` shape so
		// the `onCreated` callback works for either path.
		const result = isUpdate
			? await updateDeal.mutateAsync({ companyId, dealId: dealId as string, payload })
			: await createDeal.mutateAsync({ companyId, payload });
		onCreated?.(result);
	}, [
		config,
		values,
		customProperties,
		companyId,
		dealId,
		isUpdate,
		createDeal,
		updateDeal,
		saveDealNote,
		onCreated,
		schedulerEngaged,
		scheduledMeetingId,
		phoneCallId,
		leadServedSource,
		userSession,
	]);

	if (isLoading) {
		return (
			<div className={css(styles.state)} aria-live='polite'>
				Loading deal form…
			</div>
		);
	}
	if (loadError) {
		console.error('Deal form load error:', loadError);
		return <div className={css(styles.state)}>Could not load the deal form.</div>;
	}
	if (!config) {
		return null;
	}

	// Match legacy `DealDrawerContent` button + header copy
	// (`aida/components/Queue/DealDrawerContent/index.tsx:131-148`): label
	// flips between Create / Update (and the busy variants) based on
	// whether a `dealId` is in play.
	const headerTitle = isUpdate ? 'Update Deal' : 'Create Deal';
	const submitLabel = activeMutation.isPending
		? isUpdate
			? 'Updating…'
			: 'Creating…'
		: isUpdate
			? 'Update'
			: 'Create';

	return (
		<div className={css(styles.root)}>
			<header className={css(styles.head)}>
				<div className={css(styles.hexWrap)}>
					<HexGraphic animate={false} selected={true} className={css(styles.hex)} />
					<AnimatedLionGraphic animate={false} className={css(styles.lion)} />
				</div>
				<span className={css(styles.title)}>{headerTitle}</span>
			</header>
			<div className={css(styles.fields)}>
				{visibleFields.map(f => (
					<DealField
						key={f.id}
						field={f}
						value={values[f.id]}
						onChange={next => handleFieldChange(f.id, next)}
						error={errors[f.id]}
						disabled={isBusy}
						companyId={companyId}
						associatedContactId={associatedContactId}
						leadTimeZone={leadTimeZone}
						onSchedulerEngaged={handleSchedulerEngaged}
						onSchedulerDisengaged={handleSchedulerDisengaged}
					/>
				))}
			</div>
			<footer className={css(styles.actions)}>
				{/*
				 * Legacy ordering: primary action first (Create / Update), Cancel
				 * second. The legacy CTA bar centred its children; we keep the
				 * focused-view's right-aligned bar but match legacy ordering so
				 * the primary action is the visually leading control.
				 */}
				<Button onClick={handleCreate} disabled={isBusy}>
					{submitLabel}
				</Button>
				{onCancel && (
					<Button variant='outline' onClick={onCancel} disabled={isBusy}>
						Cancel
					</Button>
				)}
			</footer>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		background: 'var(--bg-elev)',
	},
	head: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		padding: '14px 18px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
		minHeight: 60,
	},
	hexWrap: {
		position: 'relative',
		width: 42,
		height: 42,
		flexShrink: 0,
	},
	hex: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		height: '100%',
		width: '100%',
	},
	lion: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		height: '70%',
		width: '70%',
	},
	title: {
		fontSize: '1.25rem',
		fontWeight: 600,
		color: 'var(--accent)',
	},
	fields: {
		flex: 1,
		overflowY: 'auto',
		// Match legacy `DealDrawerContent.dealForm` density: 20px top, 30px
		// horizontal, 0 bottom (the last field's spacing is owned by the
		// field itself in legacy; we approximate with a uniform gap).
		padding: '20px 30px 0',
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
	},
	actions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 8,
		padding: '12px 18px',
		borderTop: '1px solid var(--border)',
	},
	state: {
		padding: '14px 18px',
		color: 'var(--ink-muted)',
		fontSize: 13,
	},
});
